import { useEffect, useId } from 'react'

import { useStickyContext } from '@/contexts/StickyContext'
import useStickyStore from '@/store/useStickyStore'

export const PRIORITY = {
  navbar: 10,
  tabs: 20,
  tableTopBar: 30,
  tableHeader: 40
} as const

export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY]

type UseStickyParams = {
  priority: Priority
  elementRef: React.RefObject<HTMLElement>
}

const useStickyElement = ({ priority, elementRef }: UseStickyParams) => {
  const id = useId()
  const contextId = useStickyContext()
  const {
    registerElement,
    unregisterElement,
    updateElementHeight,
    calculateTopOffset
  } = useStickyStore()

  useEffect(() => {
    if (!contextId) return

    const updateHeight = () => {
      if (elementRef.current) {
        const height = elementRef.current.offsetHeight
        updateElementHeight(id, contextId, height)
      }
    }

    if (elementRef.current) {
      const height = elementRef.current.offsetHeight
      registerElement(id, contextId, priority, height)
    }

    window.addEventListener('resize', updateHeight)

    return () => {
      unregisterElement(id, contextId)
      window.removeEventListener('resize', updateHeight)
    }
  }, [
    priority,
    registerElement,
    unregisterElement,
    elementRef,
    id,
    updateElementHeight,
    contextId
  ])

  if (!contextId) return 0

  return calculateTopOffset(priority, contextId)
}

export default useStickyElement
